import { ArrowRight, Clock, Shield, Users } from 'lucide-react';
import { useState } from 'react';

import { useGetStudioLogoImage } from '~entities/studios/api/get-studio-logo-image';
import { useStudioStore } from '~entities/studios/model/studio-store';

import { SetPasswordDialog } from '~features/profile/ui/set-password-dialog';

import { Button } from '~shared/shadcn/ui/button';

const features = [
  {
    icon: Shield,
    title: 'Secure access for teachers & staff',
  },
  {
    icon: Users,
    title: 'No CRM access required',
  },
  {
    icon: Clock,
    title: 'One-time setup (takes under 30 seconds)',
  },
];

export const SetPasswordPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { currentStudio } = useStudioStore();

  const studioLogoQuery = useGetStudioLogoImage({
    studioId: currentStudio?.locationId ?? '',
  });

  const logoSrc = studioLogoQuery.data?.imageUrl;
  const logoAlt =
    studioLogoQuery.data?.studioName != null
      ? `${studioLogoQuery.data.studioName} Logo`
      : 'Enrollio Logo';

  return (
    <>
      <div className='flex min-h-screen flex-col lg:flex-row'>
        <div className='relative hidden overflow-hidden bg-secondary lg:flex lg:w-[35%] xl:w-[30%]'>
          {/* Subtle gradient overlay */}
          <div className='absolute inset-0 bg-gradient-to-br from-navy-blue via-navy-blue to-[#001220]' />

          {/* Content */}
          <div className='relative z-10 flex w-full flex-col justify-between px-12 py-2 xl:px-16'>
            <div />
            <div className='space-y-8'>
              {/* Logo */}
              <div>
                <img
                  src={
                    logoSrc ??
                    'https://storage.googleapis.com/enrollio-logo/enrollio-logo-white.png'
                  }
                  alt={logoAlt || 'Enrollio Logo'}
                  className='h-20 w-auto'
                />
              </div>

              {/* Headline */}
              <div className='space-y-2'>
                <h1 className='text-4xl font-bold leading-tight text-white xl:text-5xl'>
                  Your studio,
                  <br />
                  <span className='text-primary'>simplified.</span>
                </h1>
              </div>

              {/* Subtitle */}
              <p className='max-w-md text-lg leading-relaxed text-white/70'>
                Manage attendance, classes, and events with ease. Built for
                dance and performing arts studios.
              </p>
            </div>
            <div>
              <a
                href='https://enrollio.ai/privacy-policy'
                className='text-sm text-[#8b9aaa] transition-colors hover:text-[#b5c2dd]'
                target='_blank'
                rel='noreferrer'
              >
                Privacy Policy
              </a>
            </div>
          </div>
        </div>

        <div className='flex flex-1 flex-col items-center justify-center bg-muted/30 p-6 sm:p-10 lg:w-1/2 lg:p-12'>
          {/* Mobile logo */}
          <div className='mb-8 flex justify-center lg:hidden'>
            <img
              src={
                logoSrc ??
                'https://storage.googleapis.com/enrollio-logo/enrollio-logo-white.png'
              }
              alt={logoAlt || 'Enrollio Logo'}
              className='h-12 w-auto'
            />
          </div>

          {/* Floating card */}
          <div className='w-full max-w-md space-y-6 rounded-2xl bg-background p-2 shadow-lg sm:p-8'>
            <div className='space-y-3 text-center'>
              <h2 className='text-2xl font-bold text-navy-blue'>
                Welcome to
                <br />
                {currentStudio?.subaccountName ?? 'Enrollio Studio Portal'}
              </h2>
              <p className='text-sm leading-relaxed text-muted-foreground'>
                We&apos;ve added a secure login for studio staff so teachers and
                admins can access attendance and classes — without CRM access.
              </p>
            </div>

            {/* Features list */}
            <div className='space-y-3'>
              {features.map((feature, index) => (
                <div
                  key={index}
                  className='flex items-center gap-3 rounded-xl bg-muted/50 px-4 py-3'
                >
                  <div className='flex size-8 items-center justify-center rounded-lg bg-background'>
                    <feature.icon className='size-4 text-muted-foreground' />
                  </div>
                  <span className='text-sm text-foreground'>
                    {feature.title}
                  </span>
                </div>
              ))}
            </div>

            <div className='space-y-3 pt-2'>
              <Button
                onClick={() => {
                  setIsModalOpen(true);
                }}
                className='group h-12 w-full rounded-xl text-sm font-semibold text-secondary transition-all'
              >
                Create Password & Continue
                <ArrowRight className='ml-2 size-4 transition-transform group-hover:translate-x-0.5' />
              </Button>

              <p className='text-center text-xs text-muted-foreground'>
                You&apos;ll only need to do this once.
              </p>
            </div>
          </div>

          {/* Support footer */}
          <p className='mt-8 text-center text-xs text-muted-foreground'>
            Need help? email{' '}
            <a
              href='mailto:support@enrollio.ai'
              className='text-navy-blue/70 hover:text-navy underline underline-offset-2 transition-colors'
            >
              support@enrollio.ai
            </a>
          </p>
        </div>
      </div>

      <SetPasswordDialog open={isModalOpen} onOpenChange={setIsModalOpen} />
    </>
  );
};
